import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BandSize, BraSizeSource, CupSize, titleForBraSizeSource } from '@vestico/model';

import globalStyle from '@vestico/shared/src/layouts/utils.module.css';

import { Dropdown, DropdownOption } from './Dropdown';
import { OptionsGrid } from './OptionsGrid';
import style from './braselect.module.css';

export interface EntryValueConfig {
  digitsCount: number;
  suffix?: string;
}

export interface BraSize {
  band?: number;
  cup?: number;
}

export interface BraSelectConfiguration {
  id: string;
  name: string;
  options: BraSizeSource[];
  selectedOptionIndex: number;
  value: BraSize;
  didUpdateConfig?: (config: BraSelectConfiguration) => void;
  errorText: string;
  showErrorIfNeeded: boolean;
}

export interface BraSelectProps {
  configuration: BraSelectConfiguration;
}

export const BraSelect = ({ configuration: inputConfig }: BraSelectProps) => {
  const { t } = useTranslation('shared');
  const [configuration, setConfiguration] = useState(inputConfig);
  const { options } = configuration;
  const dropdownOptions: DropdownOption[] = useMemo(() => {
    return options.map((braSelectSource: BraSizeSource) => {
      return {
        content: (
          <span>
            {braSelectSource.imageURL} {titleForBraSizeSource(braSelectSource)}
          </span>
        ),
        imageURL: braSelectSource.imageURL,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, t]);

  const updateSelectedBraSizeOptionIndex = useCallback(
    (newIndex: number) => {
      // Don't update if indexes didn't change
      if (newIndex === configuration.selectedOptionIndex) {
        return;
      }

      const newConfig = { ...configuration };
      newConfig.selectedOptionIndex = newIndex;
      newConfig.value = {}; // reset bra size
      setConfiguration(newConfig);

      configuration.didUpdateConfig?.(newConfig);
    },
    [configuration]
  );

  const onUpdateBand = useCallback(
    (newBandIndex: number) => {
      const selectedSizeOption = options[configuration.selectedOptionIndex];
      const bandSize: BandSize = selectedSizeOption.bandSizes[newBandIndex];

      const newConfig = { ...configuration };
      const { value } = configuration;
      value.band = bandSize.standardizedValue;
      newConfig.value = value;
      setConfiguration(newConfig);
      configuration.didUpdateConfig?.(newConfig);
    },
    [configuration]
  );

  const onUpdateCup = useCallback(
    (newCupIndex: number) => {
      const selectedSizeOption = options[configuration.selectedOptionIndex];
      const cupSize: CupSize = selectedSizeOption.cupSizes[newCupIndex];

      const newConfig = { ...configuration };
      const { value } = configuration;
      value.cup = cupSize.standardizedValue;
      newConfig.value = value;
      setConfiguration(newConfig);
      configuration.didUpdateConfig?.(newConfig);
    },
    [configuration]
  );

  const didUpdateBraSizeOption = useCallback(
    (index?: number) => (index !== undefined ? updateSelectedBraSizeOptionIndex(index) : undefined),
    [updateSelectedBraSizeOptionIndex]
  );

  const braSizeOptionsDropdown = (
    <Dropdown options={dropdownOptions} selectedOptionIndex={configuration.selectedOptionIndex} didUpdateOption={didUpdateBraSizeOption} />
  );

  const bandOptionsGrid = useMemo(() => {
    const selectedSizeOption = options[configuration.selectedOptionIndex];

    const bandOptions = selectedSizeOption.bandSizes;
    if (bandOptions.length === 0) {
      return <></>;
    }

    const bandOptionsStrings = bandOptions.map((bandSize: BandSize) => {
      return bandSize.displayValue;
    });

    const selectedBraSize = configuration.value;

    let selectedOptionIndex;
    bandOptions.forEach((bandSize: BandSize, index: number) => {
      if (bandSize.standardizedValue === selectedBraSize.band) {
        selectedOptionIndex = index;
      }
    });

    return (
      <OptionsGrid
        name={t('LOC_BRA_BAND_OPTIONS_TITLE')}
        options={bandOptionsStrings}
        selectedOptionIndex={selectedOptionIndex}
        didUpdateOption={onUpdateBand}
      />
    );
  }, [configuration.selectedOptionIndex, configuration.value, onUpdateBand, options, t]);

  const cupOptionsGrid = useMemo(() => {
    const selectedSizeOption = options[configuration.selectedOptionIndex];

    const cupOptions = selectedSizeOption.cupSizes;
    if (cupOptions.length === 0) {
      return <></>;
    }

    const cupOptionsStrings = cupOptions.map((cupSize: CupSize) => {
      return cupSize.displayValue;
    });

    const selectedBraSize = configuration.value;

    let selectedOptionIndex;
    cupOptions.forEach((cupSize: CupSize, index: number) => {
      if (cupSize.standardizedValue === selectedBraSize.cup) {
        selectedOptionIndex = index;
      }
    });

    return (
      <OptionsGrid
        name={t('LOC_BRA_CUP_OPTIONS_TITLE')}
        options={cupOptionsStrings}
        selectedOptionIndex={selectedOptionIndex}
        didUpdateOption={onUpdateCup}
      />
    );
  }, [configuration.selectedOptionIndex, configuration.value, onUpdateCup, options, t]);

  const showError = useMemo(
    () => configuration.showErrorIfNeeded && (!configuration.value.band || !configuration.value.cup),
    [configuration.showErrorIfNeeded, configuration.value.band, configuration.value.cup]
  );

  return (
    <div>
      {braSizeOptionsDropdown}
      {showError && <div className={clsx(style.error, globalStyle.textSmall, globalStyle.textColorError)}>{configuration.errorText}</div>}
      {bandOptionsGrid}
      {cupOptionsGrid}
    </div>
  );
};
